import React from "react";
import type { AdminSessionSummary } from "../../../services/adminSessionsService";
import { STATUS_LABEL, STATUS_TONE, formatDateTime } from "./sessionsModel";

export interface UserSessionListProps {
  heading: string | null;
  sessions: AdminSessionSummary[] | null;
  loading: boolean;
  error: string | null;
  onOpen: (sessionId: string) => void;
}

/** Right pane: every session of the selected user, newest first. */
const UserSessionList: React.FC<UserSessionListProps> = ({
  heading,
  sessions,
  loading,
  error,
  onOpen,
}) => {
  if (!heading) {
    return (
      <div className="flex items-center justify-center border border-outline-variant/30 rounded bg-surface-container min-h-[28rem] text-sm text-slate-500 font-body">
        Select a user to see their sessions.
      </div>
    );
  }

  return (
    <div className="flex flex-col border border-outline-variant/30 rounded bg-surface-container min-h-[28rem] min-w-0">
      <div className="px-4 py-3 border-b border-outline-variant/30">
        <h3 className="font-headline text-lg font-bold text-on-surface truncate">{heading}</h3>
      </div>
      {error && <p className="p-4 text-sm text-error font-body">{error}</p>}
      {!error && loading && (
        <p className="p-4 text-xs font-label uppercase tracking-widest text-slate-500">Loading…</p>
      )}
      {!error && !loading && sessions?.length === 0 && (
        <p className="p-4 text-sm text-slate-500 font-body">This user has no sessions.</p>
      )}
      {!error && !loading && sessions && sessions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs font-label uppercase tracking-widest text-slate-500 border-b border-outline-variant/30">
                <th className="px-4 py-2 font-normal whitespace-nowrap">Attempt #</th>
                <th className="px-4 py-2 font-normal">Scenario</th>
                <th className="px-4 py-2 font-normal">Started</th>
                <th className="px-4 py-2 font-normal">Last active</th>
                <th className="px-4 py-2 font-normal text-right">Msgs</th>
                <th className="px-4 py-2 font-normal text-right">Coach</th>
                <th className="px-4 py-2 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.sessionId}
                  onClick={() => onOpen(s.sessionId)}
                  className="border-b border-outline-variant/10 hover:bg-surface-container-high cursor-pointer text-sm font-body text-on-surface"
                >
                  <td className="px-4 py-3 text-slate-500">{s.sessionNumber}</td>
                  <td className="px-4 py-3">
                    <div className="truncate max-w-[16rem]">{s.scenarioTitle ?? "Unknown scenario"}</div>
                    {s.name && <div className="truncate max-w-[16rem] text-xs text-slate-500">{s.name}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(s.createdAt)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(s.lastActiveAt)}</td>
                  <td className="px-4 py-3 text-right">{s.messageCount}</td>
                  <td className="px-4 py-3 text-right">{s.coachTurnCount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`whitespace-nowrap text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded border ${STATUS_TONE[s.status]}`}
                    >
                      {STATUS_LABEL[s.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserSessionList;
